import { Scale } from 'lucide-react';
import type { VerticalContent } from '../VerticalLandingPage';

export const legalContent: VerticalContent = {
  vertical: 'legal',
  industryLabel: 'Legal',
  heroIcon: Scale,
  heroBadge: 'For law firms and in-house legal teams',
  heroHeadline: 'Run your matters like the senior partners actually want them run.',
  heroSub:
    'Karyva replaces the matter-tracking spreadsheet with a real system — deadlines that flow through statutes of limitations, document trails that survive audit, and partner-level visibility without burying associates in status emails.',
  trustStats: [
    { value: 'Matter', label: 'lifecycle, not "projects"' },
    { value: 'Audit', label: 'trail on every document' },
    { value: '14-day', label: 'free trial, no card needed' },
  ],
  pains: [
    {
      title: 'Deadlines live in three calendars and one paralegal\'s head',
      body:
        'Statutes of limitations, court filings, discovery cutoffs, expert disclosures — when the calendaring paralegal is out, a missed date is one sticky note away from a malpractice claim.',
    },
    {
      title: 'Matter status reports are written by associates at 11pm',
      body:
        'Every Friday, every associate copies the same five fields into a shared doc. Partners read summaries that are already 48 hours stale. Nobody has time to ask which matters are actually at risk.',
    },
    {
      title: 'Document trails fall apart at audit time',
      body:
        'Was the engagement letter signed before that first call? Who approved the fee adjustment? Six months later, the partner is digging through email archives instead of running the practice.',
    },
    {
      title: 'Fee data leaks to the wrong people',
      body:
        'A summer associate doesn\'t need to see realization rates. A staff attorney shouldn\'t see partner comp. Most PM tools either show everyone everything or block them out of the matter entirely.',
    },
  ],
  bridges: [
    {
      painHook: 'Critical dates spread across multiple calendars',
      feature: 'Statute-aware deadline tracking with cascading reminders',
      body:
        'Set the SOL or filing deadline and Karyva works backward: 30-day, 14-day, 3-day reminders, automatically assigned to the right associate and paralegal. Court holidays and jurisdiction-specific tolling baked in.',
    },
    {
      painHook: 'Stale Friday status reports',
      feature: 'Live matter dashboards partners actually open',
      body:
        'Realization, hours-to-budget, key dates, document status — all live. The Monday partner meeting starts with the dashboard already on the screen, not an email chain about whose turn it was to update it.',
    },
    {
      painHook: 'Document trails that vanish at audit',
      feature: 'Immutable audit log on every status change and approval',
      body:
        'Every signed engagement letter, fee adjustment, conflict check, and document approval is recorded with the user, timestamp, and prior state. Six months from now you can prove exactly what was approved when, and by whom.',
    },
    {
      painHook: 'Fee and comp data visible to the wrong people',
      feature: 'Field-level visibility with role-scoped views',
      body:
        'Hide hours-to-budget, realization, and any custom field below a configurable role. A summer associate sees scope and deadlines; the practice group leader sees the financial picture. One matter, many lenses.',
    },
  ],
  workflowTitle: 'A matter, from intake to closeout, in Karyva',
  workflowSteps: [
    {
      when: 'Intake',
      what:
        'Conflict check completes inside the matter record. Engagement letter generated from template, sent for signature, signed copy attached automatically. Matter status flips from Intake to Active only after both sign.',
    },
    {
      when: 'Active matter',
      what:
        'Discovery deadlines populate from the case schedule. Each associate sees their assignments and hour budget; the partner sees realization and the full deadline tree. Updates flow live — no Friday status emails.',
    },
    {
      when: 'Critical date approaching',
      what:
        'A filing deadline at T-14, T-7, and T-3 fires reminders to the responsible attorney AND backs up the assignment to a second associate. Missed dates can\'t hide.',
    },
    {
      when: 'Closeout',
      what:
        'Final invoice, document retention policy applied, files moved to the archive tier — all from one closeout action. The audit trail is sealed and exportable for malpractice carriers or regulators.',
    },
  ],
  whyKaryva: [
    'Speaks matter management — not "Sprints" and "tickets" reframed for lawyers.',
    'Built-in audit log meets the documentation expectations of malpractice carriers and ethics regulators.',
    'Per-matter access control plus field-level visibility for fee and realization data.',
    'Conflict check and engagement letter workflows out of the box, not a six-month implementation.',
  ],
  testimonial: {
    quote:
      'Our managing partner now opens Karyva before her email. That alone changed how the practice runs.',
    name: 'Managing Partner (pilot customer)',
    role: 'Mid-sized litigation boutique — 40 attorneys',
  },
  logos: ['Hartwell & Stone', 'Marsh Legal Group', 'Westbridge LLP', 'Cordova Counsel'],
  faqs: [
    {
      q: 'Does this replace our practice management system (Clio, NetDocuments)?',
      a: 'Not necessarily — most firms use Karyva alongside their PMS. Karyva runs the work; the PMS continues to handle time entry, billing, and document storage. We integrate where it helps and stay out of the way otherwise.',
    },
    {
      q: 'How does it handle conflict-of-interest checks?',
      a: 'Karyva has a built-in conflict step in the intake workflow. You can wire it to your existing conflict database, or run lightweight checks in-product. The matter cannot leave Intake until conflicts are cleared and recorded.',
    },
    {
      q: 'Is the audit trail strong enough for malpractice and ethics review?',
      a: 'Yes. Every status change, document upload, approval, and field edit is recorded with user, timestamp, IP, and prior value. The log is exportable as a sealed PDF or CSV with a tamper-evident signature.',
    },
    {
      q: 'Can in-house counsel use this for non-litigation work — contracts, IP, compliance?',
      a: 'Absolutely. In-house teams use Karyva to run contract reviews, IP portfolios, compliance projects, and regulatory matters under one umbrella. Status sets are customizable, so contract-review uses Drafting → In-Review → Negotiation → Signed.',
    },
  ],
};
